// src/app/settings/accruals/components/create-leave-policy-modal/create-leave-policy-modal.component.ts

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';

import {
  AccrualFrequency,
  CreateLeavePolicyPayload,
  LeavePolicyFilterValues,
  LeaveType,
} from '../models/accruals.model';

type LeavePolicyFormModel = {
  name: string;
  leaveType: LeaveType | null;
  accrualFrequency: AccrualFrequency | null;
  weeklyAccrualDay: number | null;
  hoursPerYear: number | null;
  maxBalance: number | null;
  maxCarryOver: number | null;
  allowNegativeBalance: 'true' | 'false';
};

@Component({
  selector: 'app-create-leave-policy-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-leave-policy-modal.component.html',
  styleUrls: ['./create-leave-policy-modal.component.scss'],
})
export class CreateLeavePolicyModalComponent implements OnChanges {
  @Input() open = false;
  @Input() isSaving = false;
  @Input() filterValues: LeavePolicyFilterValues | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<CreateLeavePolicyPayload>();

  formModel: LeavePolicyFormModel = this.getEmptyForm();
  submitted = false;

  weekDayOptions: Array<{ label: string; value: number }> = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterValues']) {
      this.weekDayOptions = this.buildWeekDayOptions();
    }

    if (changes['open']?.currentValue === true) {
      this.resetForm();
    }
  }

  get leaveTypeOptions(): LeaveType[] {
    return this.filterValues?.leaveTypes ?? [];
  }

  get accrualFrequencyOptions(): AccrualFrequency[] {
    return this.filterValues?.accrualFrequencies ?? [];
  }

  get showWeeklyAccrualDay(): boolean {
    return this.formModel.accrualFrequency === 'Weekly';
  }

  onBackdropClick(): void {
    if (this.isSaving) return;
    this.handleClose();
  }

  handleClose(): void {
    this.resetForm();
    this.close.emit();
  }

  onAccrualFrequencyChange(value: AccrualFrequency | null): void {
    this.formModel.accrualFrequency = value;

    if (value !== 'Weekly') {
      this.formModel.weeklyAccrualDay = null;
    }
  }

  submit(form: NgForm): void {
    this.submitted = true;

    if (!form.valid) {
      form.control.markAllAsTouched();
      return;
    }

    if (!this.formModel.leaveType || !this.formModel.accrualFrequency) {
      return;
    }

    if (this.showWeeklyAccrualDay && this.formModel.weeklyAccrualDay === null) {
      return;
    }

    const payload: CreateLeavePolicyPayload = {
      name: this.formModel.name.trim(),
      leaveType: this.formModel.leaveType,
      accrualFrequency: this.formModel.accrualFrequency,
      weeklyAccrualDay: this.showWeeklyAccrualDay ? this.formModel.weeklyAccrualDay : null,
      hoursPerYear: Number(this.formModel.hoursPerYear ?? 0),
      maxBalance: Number(this.formModel.maxBalance ?? 0),
      maxCarryOver: Number(this.formModel.maxCarryOver ?? 0),
      allowNegativeBalance: this.formModel.allowNegativeBalance === 'true',
    };

    this.save.emit(payload);
  }

  private resetForm(): void {
    this.submitted = false;
    this.formModel = this.getEmptyForm();
  }

  private getEmptyForm(): LeavePolicyFormModel {
    return {
      name: '',
      leaveType: null,
      accrualFrequency: null,
      weeklyAccrualDay: null,
      hoursPerYear: null,
      maxBalance: null,
      maxCarryOver: null,
      allowNegativeBalance: 'false',
    };
  }

  private buildWeekDayOptions(): Array<{ label: string; value: number }> {
    const source = this.filterValues?.weekDays ?? [];

    return source.map((day, index) => ({
      label: day,
      value: this.toApiWeekDay(index),
    }));
  }

  /**
   * API mapping:
   * 1 = Monday
   * 2 = Tuesday
   * 3 = Wednesday
   * 4 = Thursday
   * 5 = Friday
   * 6 = Saturday
   * 0 = Sunday
   *
   * UI filter-values comes back:
   * Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday
   */
  private toApiWeekDay(index: number): number {
    const day = (this.filterValues?.weekDays?.[index] ?? '').toLowerCase();

    switch (day) {
      case 'sunday':
        return 0;
      case 'monday':
        return 1;
      case 'tuesday':
        return 2;
      case 'wednesday':
        return 3;
      case 'thursday':
        return 4;
      case 'friday':
        return 5;
      case 'saturday':
        return 6;
      default:
        return index;
    }
  }
}